<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps({
  label: String,
  labelId: String,
  color: String,
  vertical: {
    type: Boolean,
    default: false,
  },
  modelValue: {},
  value: {},
});

const emit = defineEmits(['update:modelValue']);

const modelProxy = computed({
  get: () => props.modelValue,
  set(val) {},
});

const isSelected = computed(() => props.modelValue == props.value);

const classes = computed(() => {
  return {
    'radio-wrapper': true,
    'radio-wrapper--selected': isSelected.value,
    [`radio-wrapper--${props.color}`]: props.color,
    'flex-col': props.vertical,
    'gap-y-2': props.vertical,
  };
});
</script>

<template>
  <label :class="classes">
    <input
      class="radio-input"
      type="radio"
      :aria-labelledby="labelId"
      :data-colorscheme="color"
      v-bind="$attrs"
      :value="value"
      v-model="modelProxy"
    />
    
    <!-- Custom radio indicator -->
    <span class="radio-indicator" :class="{ 'radio-indicator--selected': isSelected }">
      <span class="radio-indicator__dot" :class="{ 'radio-indicator__dot--visible': isSelected }"></span>
    </span>

    <span :id="labelId" v-html="label" class="radio-label"></span>
  </label>
</template>

<style scoped lang="postcss">
.radio-wrapper {
  display: flex;
  align-items: center;
  padding: 0.75rem 0.5rem;
  padding-left: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
  border-radius: 6px;
}

.radio-wrapper:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.radio-wrapper--compact {
  padding: 0.5rem 0;
}

.radio-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.radio-indicator {
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 50%;
  border: 2px solid #c4c4c4;
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  background: white;
}

.radio-wrapper:hover .radio-indicator {
  border-color: #999;
}

.radio-indicator--selected {
  border-color: currentColor;
}

.radio-indicator__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: currentColor;
  transform: scale(0);
  transition: transform 0.15s ease;
}

.radio-indicator__dot--visible {
  transform: scale(1);
}

/* Color schemes */
.radio-wrapper--purple .radio-indicator--selected {
  border-color: #8d3e79;
  color: #8d3e79;
}

.radio-wrapper--green .radio-indicator--selected {
  border-color: #4c8201;
  color: #4c8201;
}

.radio-wrapper--yellow .radio-indicator--selected {
  border-color: #ab8008;
  color: #ab8008;
}

.radio-wrapper--blue .radio-indicator--selected {
  border-color: #21806e;
  color: #21806e;
}

.radio-label {
  flex: 1;
  font-size: 0.9375rem;
  line-height: 1.4;
  color: #333;
  pointer-events: none;
}

.radio-wrapper--selected .radio-label {
  font-weight: 500;
  color: #111;
}

/* Focus states for accessibility */
.radio-input:focus-visible + .radio-indicator {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}
</style>
